import { addCalendarDays, formatDateOnly } from '@dataroom/shared';
import type {
  DdRequest,
  ExclusivityView,
  LoiReviewItem,
  QuestionView,
  SellerDecision,
} from '@dataroom/shared';
import { requestState } from '@/lib/checklist';

/** The seller dashboard's "Due soon" window: overdue items and those due within this many days. */
export const DUE_SOON_DAYS = 7;
/** At most this many items are listed; the rest are counted and linked. */
export const DUE_SOON_MAX = 8;

export interface DueItem {
  req: DdRequest;
  overdue: boolean;
}

export interface DueSoon {
  /** The first {@link DUE_SOON_MAX}, overdue first, then by due date. */
  items: DueItem[];
  /** Every item due soon or overdue (>= items.length). */
  total: number;
}

/**
 * Items the seller still has to provide that are overdue or due within
 * {@link DUE_SOON_DAYS} days of `today` (YYYY-MM-DD, the room's calendar day).
 * An item's `dueDate` already falls back to its phase's due date (server-side).
 * Only `missing` items count: one waiting on review is with the deal team, and
 * a covered or "doesn't apply" one is resolved.
 */
export function dueSoon(requests: readonly DdRequest[], today: string): DueSoon {
  const horizon = addCalendarDays(today, DUE_SOON_DAYS);
  const due = requests
    .filter((r) => r.dueDate && r.dueDate <= horizon && requestState(r) === 'missing')
    .map((req): DueItem => ({ req, overdue: (req.dueDate as string) < today }))
    .sort(
      (a, b) =>
        (a.req.dueDate as string).localeCompare(b.req.dueDate as string) ||
        a.req.title.localeCompare(b.req.title),
    );
  return { items: due.slice(0, DUE_SOON_MAX), total: due.length };
}

/** "Overdue — was due Oct 20, 2026" / "Due Oct 28, 2026 (phase deadline)". */
export function dueLabel(item: DueItem): string {
  const date = formatDateOnly(item.req.dueDate);
  const inherited = item.req.dueDateInherited ? ' (phase deadline)' : '';
  return item.overdue ? `Overdue — was due ${date}${inherited}` : `Due ${date}${inherited}`;
}

/** Buyer questions routed to the seller that still need their answer (not closed, no draft yet). */
export function questionsAwaitingAnswer(questions: readonly QuestionView[] | undefined): number {
  return (questions ?? []).filter((q) => q.status !== 'closed' && !q.answer).length;
}

const awaitsMyVote = (v: SellerDecision | null | undefined): boolean =>
  !!v && v.viewerCanVote && v.viewerVote === null;

/**
 * Votes an OWNER has yet to cast: counters and declines of a letter of intent, and
 * the accepted LOI or exclusivity extension waiting on the owners. Mount only for
 * an owner — the sources (LOI listing, exclusivity view) answer sellers alone.
 */
export function votesAwaitingOwner(
  lois: readonly LoiReviewItem[] | undefined,
  exclusivity: ExclusivityView | undefined,
): number {
  const responses = (lois ?? []).filter(
    (l) => l.sellerVote?.kind === 'loi_response' && awaitsMyVote(l.sellerVote),
  ).length;
  const pending = awaitsMyVote(exclusivity?.sellerVote) ? 1 : 0;
  return responses + pending;
}

/** The Checklist tab, narrowed to a phase's missing items (or all of its items). */
export function checklistLink(
  roomId: string,
  opts: { phaseId?: string | null; missingOnly?: boolean } = {},
): string {
  const q = new URLSearchParams();
  if (opts.missingOnly) q.set('filter', 'missing');
  if (opts.phaseId) q.set('phase', opts.phaseId);
  const qs = q.toString();
  return `/rooms/${roomId}/checklist${qs ? `?${qs}` : ''}`;
}
